import type { ApiClient } from '../../core/api-client.js';
import { ApiError } from '../../core/api-client.js';
import type {
  ParceiroAutenticado,
  Cupom,
  Comissao,
  AssinaturaPgArt,
  SolicitacaoAssistencia,
  NovaSolicitacaoAssistencia,
} from '@pgcustom/shared-types';

interface RespostaLoginPortal {
  parceiro: ParceiroAutenticado;
  expiraEm: string;
}

interface RespostaMePortal {
  parceiro: ParceiroAutenticado;
}

interface RespostaCupons {
  cupons: Cupom[];
}

interface RespostaComissoes {
  comissoes: Comissao[];
}

interface RespostaAssinaturas {
  assinaturas: AssinaturaPgArt[];
}

interface RespostaAssistencias {
  solicitacoes: SolicitacaoAssistencia[];
}

interface RespostaAssistencia {
  solicitacao: SolicitacaoAssistencia;
}

/**
 * Adapter do Portal do Parceiro no frontend — único ponto que conhece o
 * formato de `/api/v1/portal-parceiros/*`
 * (`apps/api/src/modules/parceiros-portal/parceiros-portal.routes.ts`).
 * Recebe seu próprio `ApiClient` (nunca o mesmo usado pelo ERP interno)
 * — a sessão do parceiro vive num cookie próprio
 * (`pg_portal_parceiro_session`) e um 401 aqui nunca deve derrubar uma
 * sessão de `identidade.usuarios` que porventura exista na mesma aba,
 * nem vice-versa (Conflito 1/2 já resolvido no backend, espelhado aqui).
 */
export class PortalParceiroApi {
  constructor(private readonly api: ApiClient) {}

  async login(email: string, senha: string): Promise<ParceiroAutenticado> {
    const resposta = await this.api.post<RespostaLoginPortal>('/portal-parceiros/login', { email, senha });
    return resposta.parceiro;
  }

  async logout(): Promise<void> {
    await this.api.post<void>('/portal-parceiros/logout');
  }

  /** Conclui a troca de senha obrigatória (`ParceiroAutenticado.deveTrocarSenha`) — exige a senha atual (a temporária) para confirmar identidade na sessão já ativa. */
  async trocarSenhaObrigatoria(senhaAtual: string, novaSenha: string): Promise<void> {
    await this.api.post<void>('/portal-parceiros/me/trocar-senha-obrigatoria', { senhaAtual, novaSenha });
  }

  /** `null` quando não há sessão válida (401) — estado esperado de um visitante não autenticado, não um erro. */
  async obterParceiroAtual(): Promise<ParceiroAutenticado | null> {
    try {
      const resposta = await this.api.get<RespostaMePortal>('/portal-parceiros/me');
      return resposta.parceiro;
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 401) return null;
      throw erro;
    }
  }

  async meusCupons(): Promise<Cupom[]> {
    const resposta = await this.api.get<RespostaCupons>('/portal-parceiros/me/cupons');
    return resposta.cupons;
  }

  async minhasComissoes(): Promise<Comissao[]> {
    const resposta = await this.api.get<RespostaComissoes>('/portal-parceiros/me/comissoes');
    return resposta.comissoes;
  }

  async minhasAssinaturas(): Promise<AssinaturaPgArt[]> {
    const resposta = await this.api.get<RespostaAssinaturas>('/portal-parceiros/me/assinaturas');
    return resposta.assinaturas;
  }

  async minhasAssistencias(): Promise<SolicitacaoAssistencia[]> {
    const resposta = await this.api.get<RespostaAssistencias>('/portal-parceiros/me/assistencias');
    return resposta.solicitacoes;
  }

  async solicitarAssistencia(dado: Omit<NovaSolicitacaoAssistencia, 'parceiroId'>): Promise<SolicitacaoAssistencia> {
    const resposta = await this.api.post<RespostaAssistencia>('/portal-parceiros/me/assistencias', dado);
    return resposta.solicitacao;
  }
}
